import { formatPattern, parsePattern } from './datetime'

/**
 * Splits a string the way lodash's `words` does for our inputs: on runs of
 * non-alphanumerics, and at letter/digit boundaries. Apostrophes are dropped
 * and diacritics folded, matching lodash's `deburr` step in `startCase`.
 */
function words(value: string): string[] {
    return value
        .normalize('NFD')
        .replace(/[̀-ͯ]/g, '')
        .replace(/['’]/g, '')
        .replace(/([a-zA-Z])(\d)/g, '$1 $2')
        .replace(/(\d)([a-zA-Z])/g, '$1 $2')
        .split(/[^a-zA-Z0-9]+/)
        .filter(Boolean)
}

/** Title-cases a string, e.g. 'blue belt' -> 'Blue Belt'. */
export function capitalize(string: string) {
    return words(String(string ?? '').toLowerCase())
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ')
}

export function formatDate(date: string | null, format: string) {
    return date !== null ? formatPattern(new Date(date), format) : date
}

/**
 * Coarse relative time: "just now", "4h ago", "3 Feb".
 *
 * Anything older than a week falls back to an absolute date, because "37 days
 * ago" is harder to place than the date itself. `now` is a parameter so this
 * stays testable.
 */
export function formatRelative(date: string | null | undefined, now: Date = new Date()) {
    if (!date) return null

    // API timestamps arrive as 'YYYY-MM-DD HH:mm:ss', which Safari refuses to
    // parse with a space separator.
    const parsed = new Date(String(date).replace(' ', 'T'))
    if (Number.isNaN(parsed.getTime())) return null

    const seconds = Math.round((now.getTime() - parsed.getTime()) / 1000)

    // A clock-skewed future timestamp reads as current rather than negative.
    if (seconds < 60) return 'just now'
    if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`
    if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`
    if (seconds < 604800) return `${Math.floor(seconds / 86400)}d ago`

    return formatPattern(parsed, 'DD/MM/YYYY')
}

export function formatTime(time: string | null, currentFormat: string, format: string) {
    if (time === null) return time
    const parsed = parsePattern(time, currentFormat)
    return parsed ? formatPattern(parsed, format) : time
}

/**
 * Floors to `precision` decimal places without the floating-point drift of
 * `Math.floor(n * 100) / 100` — shifts the exponent in the string form, which
 * is the same trick lodash's `floor` uses.
 */
function floorTo(value: number, precision: number): number {
    let pair = `${value}e`.split('e')
    const shifted = Math.floor(Number(`${pair[0]}e${Number(pair[1]) + precision}`))
    pair = `${shifted}e`.split('e')
    return Number(`${pair[0]}e${Number(pair[1]) - precision}`)
}

/** Returns null for 0/null/undefined, matching the previous behaviour. */
export function convertToDollars(amount: number | null | undefined) {
    if (amount) {
        return `$ ${floorTo(amount, 2)}`
    }

    return null
}
export function getInitials(name: string) {
    const names = name.split(' ')
    if (names.length === 0) return ''
    const firstInitial = names[0].charAt(0)
    const lastInitial = names[names.length - 1].charAt(0)
    return `${firstInitial} ${lastInitial}`
}
export function cx(options1 = {}, options2 = {}) {
    let classNames = typeof options1 === 'string' ? options1 : ''

    for (const [className, shouldAdd] of Object.entries(
        typeof options1 === 'string' ? options2 : options1
    )) {
        if (shouldAdd) {
            classNames += ` ${className}`
        }
    }

    return classNames.trim()
}

export function isColorDark(color: string) {
    // Convert color to RGB
    let r, g, b
    if (color.startsWith('#')) {
        r = parseInt(color.slice(1, 3), 16)
        g = parseInt(color.slice(3, 5), 16)
        b = parseInt(color.slice(5, 7), 16)
    } else {
        const parts = color.replace(/rgba|\(|\)|\s/g, '').split(',')
        ;[r, g, b] = parts.map(Number)
    }

    // Calculate brightness
    const brightness = (r * 299 + g * 587 + b * 114) / 1000

    // Return true if color is dark
    return brightness < 128
}

export function pluralize(word: string, count = 1) {
    if (count == 1) {
        return word
    }
    if (
        count !== 1 ||
        word.endsWith('s') ||
        word.endsWith('x') ||
        word.endsWith('z') ||
        word.endsWith('ch') ||
        word.endsWith('sh')
    ) {
        return `${word}s`
    }

    // Determine the plural form based on common rules
    switch (word.charAt(word.length - 1)) {
        case 'y':
            if (
                word.charAt(word.length - 2) !== 'a' &&
                word.charAt(word.length - 2) !== 'e' &&
                word.charAt(word.length - 2) !== 'i' &&
                word.charAt(word.length - 2) !== 'o' &&
                word.charAt(word.length - 2) !== 'u'
            ) {
                return `${word.slice(0, -1)}ies`
            } else {
                return `${word}s`
            }
        case 'f':
            return `${word.slice(0, -1)}ves`
        case 'fe':
            return `${word.slice(0, -2)}ves`
        default:
            return `${word}s`
    }
}
